/**
 * ReadonlyModeContext
 *
 * 全局只读模式状态管理
 * 用于控制 List Page 和 Flyout 的只读状态
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

interface ReadonlyModeContextValue {
  /** 当前是否处于只读模式 */
  isReadonly: boolean;

  /** 设置只读模式 */
  setReadonly: (readonly: boolean) => void;

  /** 切换只读模式 */
  toggleReadonly: () => void;
}

const ReadonlyModeContext = createContext<ReadonlyModeContextValue | undefined>(undefined);

export interface ReadonlyModeProviderProps {
  children: React.ReactNode;
  /** 初始只读状态 */
  defaultReadonly?: boolean;
}

/**
 * 只读模式 Provider
 *
 * @example
 * ```tsx
 * <ReadonlyModeProvider>
 *   <App />
 * </ReadonlyModeProvider>
 * ```
 */
export function ReadonlyModeProvider({
  children,
  defaultReadonly = false,
}: ReadonlyModeProviderProps) {
  const [isReadonly, setIsReadonly] = useState(defaultReadonly);

  const setReadonly = useCallback((readonly: boolean) => {
    console.log('[ReadonlyModeContext] Setting readonly mode:', readonly);
    setIsReadonly(readonly);
  }, []);

  const toggleReadonly = useCallback(() => {
    setIsReadonly((prev) => {
      const newValue = !prev;
      console.log('[ReadonlyModeContext] Toggling readonly mode:', newValue);
      return newValue;
    });
  }, []);

  const value: ReadonlyModeContextValue = {
    isReadonly,
    setReadonly,
    toggleReadonly,
  };

  return (
    <ReadonlyModeContext.Provider value={value}>
      {children}
    </ReadonlyModeContext.Provider>
  );
}

/**
 * 使用只读模式 Hook
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isReadonly, setReadonly } = useReadonlyMode();
 *
 *   return (
 *     <Button disabled={isReadonly}>
 *       编辑
 *     </Button>
 *   );
 * }
 * ```
 */
export function useReadonlyMode(): ReadonlyModeContextValue {
  const context = useContext(ReadonlyModeContext);

  if (!context) {
    throw new Error('useReadonlyMode must be used within ReadonlyModeProvider');
  }

  return context;
}
